import React, { useState } from 'react';
import { Modal, Form, Input, Button, Typography, Alert } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import api from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const REQUIRED_DOMAIN = '@vitreousco.com';
const MAX_DISMISSES = 4;

interface EmailMigrationGateProps {
  /** When optional reminder was dismissed this session, parent hides the gate */
  onDismissedOptional?: () => void;
}

export const EmailMigrationGate: React.FC<EmailMigrationGateProps> = ({ onDismissedOptional }) => {
  const { user, refetchUser } = useAuth();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const rolesRequiringMigration = ['admin', 'agent', 'developer'];
  const appliesToUser = user && rolesRequiringMigration.includes(user.role);
  const needsUpdate = appliesToUser && user.email_needs_update;
  const required = appliesToUser && user.email_update_required;

  const handleUpdateEmail = async (values: { new_email: string }) => {
    const newEmail = (values.new_email || '').trim().toLowerCase();
    if (!newEmail.endsWith(REQUIRED_DOMAIN)) {
      toast.error(`Email must end with ${REQUIRED_DOMAIN}`);
      return;
    }
    setSubmitting(true);
    try {
      await api.put('/api/users/profile', { email: newEmail });
      toast.success('Email updated successfully.');
      await refetchUser();
      form.resetFields();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to update email';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemindLater = async () => {
    setDismissing(true);
    try {
      await api.post('/api/auth/email-migration-dismissed');
      onDismissedOptional?.();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setDismissing(false);
    }
  };

  if (!needsUpdate) return null;

  const dismissCount = Math.min(MAX_DISMISSES, Math.max(0, user?.email_migration_dismiss_count ?? 0));
  const remaining = MAX_DISMISSES - dismissCount;

  const footer = required
    ? null
    : [
        <Button key="later" onClick={handleRemindLater} loading={dismissing}>
          Remind me later
        </Button>,
        <Button key="update" type="primary" onClick={() => form.submit()} loading={submitting}>
          Update email now
        </Button>,
      ];

  return (
    <Modal
      title="Update your email address"
      open={true}
      closable={!required}
      maskClosable={!required}
      footer={footer}
      width={480}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        message={
          required
            ? 'You must update your email to a Vitreous address to continue using the platform.'
            : 'Please update your email to use a Vitreous address (@vitreousco.com).'
        }
        style={{ marginBottom: 16 }}
      />
      {!required && (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
          Bypasses used: <strong>{dismissCount} of {MAX_DISMISSES}</strong>
          {remaining > 0 && ` — ${remaining} postponement${remaining === 1 ? '' : 's'} left before update is required.`}
        </Typography.Paragraph>
      )}
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Current email: <strong>{user?.email}</strong>
      </Typography.Paragraph>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleUpdateEmail}
        initialValues={{ new_email: user?.email || '' }}
      >
        <Form.Item
          name="new_email"
          label="New email"
          rules={[
            { required: true, message: 'Enter your new email' },
            { type: 'email', message: 'Enter a valid email' },
            {
              validator: (_, value) => {
                const v = (value || '').toLowerCase();
                if (v && !v.endsWith(REQUIRED_DOMAIN)) {
                  return Promise.reject(new Error(`Email must end with ${REQUIRED_DOMAIN}`));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input prefix={<MailOutlined />} placeholder={`you${REQUIRED_DOMAIN}`} />
        </Form.Item>
        {required && (
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting} block>
              Update email and continue
            </Button>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};
